const CARD_WIDTH_MM = 85.6;
const CARD_HEIGHT_MM = 53.98;
/** Match React IdCard (CR80 @ 96dpi) for PDF/HTML parity */
const CARD_PX_W = 324;
const CARD_PX_H = Math.round((CARD_PX_W * CARD_HEIGHT_MM) / CARD_WIDTH_MM);

const NDC_GREEN = '#006B3F';
const NDC_RED = '#CE1126';
const PURE_BLACK = '#000000';
const PURE_WHITE = '#FFFFFF';
const TEXT_GRAY = '#333333';

/** Public folder filenames — also exported for PDF/HTML asset inlining */
export const TEIN_ATU_BADGE_FILE = 'accra-technical-university-logo.jpg';
export const NDC_LOGO_FILE = 'ndc-logo.svg';

/** Resolve `/public` files for print preview (same origin as the app). */
function absolutePublicAsset(filename) {
  const pub = (
    (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL) ||
    ''
  ).replace(/\/$/, '');
  const path = pub ? `${pub}/${filename}` : `/${filename}`;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${path}`;
  }
  return path;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function formatExpiry(schoolCompletionDate) {
  if (schoolCompletionDate) {
    const d = new Date(schoolCompletionDate);
    const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    return `${months[d.getMonth()]} ${d.getFullYear()}`;
  }
  return `DEC ${new Date().getFullYear()}`;
}

function memberDisplay(member) {
  const fullName = [member.first_name, member.last_name].filter(Boolean).join(' ') || 'Member';
  const idNumber = member.member_id || member.association_number || '-';
  const institution = 'Accra Technical University';
  const expiry = formatExpiry(member.school_completion_date);
  const verifyUrl = `${window.location.origin}/verify/${member.id}`;
  const a = (member.first_name || '').trim().slice(0, 1);
  const b = (member.last_name || '').trim().slice(0, 1);
  const initials = `${a}${b}`.toUpperCase() || (a || 'M').toUpperCase();

  return {
    fullName: escapeHtml(fullName),
    idNumber: escapeHtml(idNumber),
    institution: escapeHtml(institution),
    expiry: escapeHtml(expiry),
    verifyUrl: escapeHtml(verifyUrl),
    initials: escapeHtml(initials),
  };
}

function renderPhoto(photoDataUrl, initials) {
  if (typeof photoDataUrl === 'string' && photoDataUrl.startsWith('data:')) {
    return `<img src="${photoDataUrl}" alt="" class="member-photo-img" />`;
  }
  return `<span class="member-photo-fallback">${initials}</span>`;
}

/**
 * @param {{ ndcDataUrl?: string, teinDataUrl?: string }} [inlinePublicAssets] — data URLs from same-origin fetch (PDF export); avoids broken iframe loads
 */
function renderFront(member, photoDataUrl, qrDataUrl, inlinePublicAssets = {}) {
  const d = memberDisplay(member);
  const qrSrc = qrDataUrl || `https://api.qrserver.com/v1/create-qr-code/?size=88x88&data=${d.verifyUrl}`;
  const ndcSrc =
    inlinePublicAssets.ndcDataUrl || absolutePublicAsset(NDC_LOGO_FILE);
  const teinSrc =
    inlinePublicAssets.teinDataUrl || absolutePublicAsset(TEIN_ATU_BADGE_FILE);
  const ndcLogoUrl = escapeHtml(ndcSrc);
  const teinAtuUrl = escapeHtml(teinSrc);
  return `
    <div class="id-card front">
      <div class="bg-gradient"></div>
      <svg class="bg-shards" viewBox="0 0 ${CARD_PX_W} ${CARD_PX_H}" preserveAspectRatio="none" aria-hidden="true">
        <polygon points="0,0 140,0 90,95" fill="${PURE_BLACK}" fill-opacity="0.14" />
        <polygon points="${CARD_PX_W},0 190,0 255,70" fill="${PURE_WHITE}" fill-opacity="0.11" />
        <polygon points="${CARD_PX_W},${CARD_PX_H} 170,${CARD_PX_H} 215,130" fill="${PURE_BLACK}" fill-opacity="0.12" />
        <polygon points="0,${CARD_PX_H} 110,${CARD_PX_H} 55,150" fill="${PURE_WHITE}" fill-opacity="0.07" />
        <polygon points="150,40 300,20 270,110" fill="${PURE_BLACK}" fill-opacity="0.09" />
        <polygon points="60,130 210,160 130,${CARD_PX_H}" fill="${PURE_WHITE}" fill-opacity="0.06" />
        <polygon points="0,80 70,0 100,90" fill="${PURE_BLACK}" fill-opacity="0.08" />
        <polygon points="240,80 ${CARD_PX_W},40 ${CARD_PX_W},120" fill="${PURE_WHITE}" fill-opacity="0.05" />
      </svg>
      <div class="card-content">
        <div class="card-header">
          <div class="logo-cluster">
            <div class="logo-wrap logo-ndc"><img class="logo-img" src="${ndcLogoUrl}" alt="" /></div>
            <div class="logo-wrap logo-tein"><img class="logo-img logo-tein-img" src="${teinAtuUrl}" alt="" /></div>
          </div>
          <div class="org-wrap">
            <div class="org-title">TERTIARY EDUCATION INSTITUTIONS NETWORK</div>
            <div class="org-subtitle">Membership Card</div>
          </div>
        </div>
        <div class="member-row">
          <div class="member-col-left">
            <div class="member-photo">${renderPhoto(photoDataUrl, d.initials)}</div>
            <div class="member-initials">${d.initials}</div>
            <div class="member-fullname">${d.fullName}</div>
          </div>
          <div class="member-meta">
            <div><strong>NAME:</strong> ${d.fullName}</div>
            <div><strong>INSTITUTION:</strong> ${d.institution}</div>
            <div><strong>MEMBER ID:</strong> ${d.idNumber}</div>
            <div><strong>EXPIRY:</strong> ${d.expiry}</div>
          </div>
        </div>
        <div class="card-footer">
          <div class="qr-row-only">
            <div class="qr-box"><img alt="QR code" src="${qrSrc}" /></div>
          </div>
          <div class="motto">UNITY, STABILITY, AND DEVELOPMENT</div>
        </div>
      </div>
    </div>
  `;
}

function renderBack() {
  return `
    <div class="id-card back">
      <div class="stripe"></div>
      <div class="barcode"></div>
      <div class="back-content">
        <div>This card remains the property of the NDC TEIN. If found, please return to any NDC office.</div>
        <div class="contact">Website: www.ndc.org.gh/tein</div>
        <div class="contact">Email: tein@ndc.org.gh</div>
        <div class="contact">Phone: +233 55 728 7496</div>
        <div class="hq-title">National Headquarters:</div>
        <div>P.O. Box AN 5825, Adabraka, Accra, Ghana</div>
        <div class="signature-wrap">
          <div class="signature-line"></div>
          <div>Authorized Signature</div>
        </div>
      </div>
    </div>
  `;
}

function baseStyles() {
  return `
    @page { size: A4 portrait; margin: 10mm; }
    * { box-sizing: border-box; }
    body { margin: 0; font-family: "Montserrat", "Roboto", Arial, sans-serif; background: #f6f7f8; }
    .sheet { padding: 10mm; }
    .card-pair { display: grid; grid-template-columns: repeat(2, ${CARD_WIDTH_MM}mm); gap: 10mm; justify-content: center; }
    .id-card {
      width: ${CARD_WIDTH_MM}mm;
      height: ${CARD_HEIGHT_MM}mm;
      border: 0.25mm solid #000;
      border-radius: 3.18mm;
      overflow: hidden;
      position: relative;
      background: #fff;
    }
    .front .bg-gradient {
      position: absolute; inset: 0;
      background: linear-gradient(135deg, ${NDC_RED} 0%, ${NDC_GREEN} 100%);
    }
    .front .bg-shards { position: absolute; inset: 0; width: 100%; height: 100%; }
    .card-content { position: relative; padding: 3.5mm; height: 100%; color: ${PURE_WHITE}; display: flex; flex-direction: column; }
    .card-header {
      display: flex; justify-content: flex-start; align-items: center; gap: 1.8mm;
      margin-bottom: 2mm;
    }
    .logo-cluster { display: flex; align-items: center; gap: 1.2mm; flex-shrink: 0; }
    .logo-wrap {
      width: 10.5mm; height: 10.5mm; border-radius: 50%; border: 0.8mm solid ${PURE_WHITE};
      background: ${PURE_WHITE}; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
      overflow: hidden; padding: 0.6mm;
    }
    .logo-wrap.logo-tein { background: transparent; padding: 0; box-shadow: 0 0.15mm 0.8mm rgba(0,0,0,0.2); }
    .logo-wrap .logo-img { width: 100%; height: 100%; object-fit: contain; display: block; }
    .logo-wrap.logo-tein .logo-tein-img { object-fit: cover; object-position: center; }
    .org-wrap { flex: 1; min-width: 0; text-align: left; }
    .org-title { font-size: 2.65mm; font-weight: 700; line-height: 1.18; letter-spacing: 0.04mm; text-transform: uppercase; }
    .org-subtitle { font-size: 1.75mm; font-weight: 400; line-height: 1.25; letter-spacing: 0.03mm; text-transform: uppercase; margin-top: 0.35mm; }
    .member-row { display: flex; gap: 2.5mm; align-items: stretch; flex: 1; min-height: 0; }
    .member-col-left {
      display: flex; flex-direction: column; align-items: center; justify-content: flex-start;
      width: 23mm; flex-shrink: 0;
    }
    .member-photo {
      width: 18mm; height: 18mm; aspect-ratio: 1 / 1; flex-shrink: 0;
      border-radius: 50%; border: 1mm solid ${PURE_WHITE};
      overflow: hidden; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.2);
      box-sizing: border-box;
    }
    .member-photo-img { width: 100%; height: 100%; object-fit: cover; }
    .member-photo-fallback { font-size: 4.2mm; font-weight: 700; }
    .member-initials { font-size: 4mm; font-weight: 800; margin-top: 1mm; letter-spacing: 0.2mm; line-height: 1; }
    .member-fullname {
      font-size: 1.55mm; font-weight: 500; text-align: center; line-height: 1.2; margin-top: 0.6mm;
      text-transform: uppercase; word-break: break-word;
    }
    .member-meta {
      font-size: 2.1mm; line-height: 1.5; flex: 1; min-width: 0; min-height: 0;
      display: flex; flex-direction: column; justify-content: space-between;
      padding: 0.9mm 0 1.1mm;
    }
    .member-meta > div { margin: 0; flex-shrink: 0; }
    .member-meta strong { font-weight: 700; margin-right: 0.55mm; }
    .card-footer {
      margin-top: auto;
      display: flex;
      flex-direction: column;
      gap: 1.2mm;
      align-items: stretch;
      padding-top: 0.6mm;
    }
    .qr-row-only { display: flex; justify-content: flex-end; width: 100%; }
    .qr-box {
      width: 13mm; height: 13mm; border: 0.5mm solid ${NDC_RED}; background: #fff; padding: 0.5mm; flex-shrink: 0;
      border-radius: 1mm;
      box-shadow: 0 0.15mm 0.6mm rgba(0,0,0,0.18);
    }
    .qr-box img { width: 100%; height: 100%; display: block; }
    .motto {
      text-align: center; font-size: 1.75mm; font-weight: 500;
      letter-spacing: 0.05mm; text-transform: uppercase;
    }
    .back { display: flex; flex-direction: column; color: ${TEXT_GRAY}; }
    .stripe { height: 8.5mm; background: ${PURE_BLACK}; flex-shrink: 0; }
    .barcode {
      height: 7.3mm; margin: 2mm 3.2mm 1.2mm;
      background: repeating-linear-gradient(90deg, ${PURE_BLACK} 0, ${PURE_BLACK} 0.4mm, transparent 0.4mm, transparent 0.8mm);
      flex-shrink: 0;
    }
    .back-content { padding: 0 3.2mm 1.9mm; font-size: 1.9mm; line-height: 1.24; }
    .contact { line-height: 1.2; }
    .hq-title { margin-top: 0.8mm; }
    .signature-wrap { margin-top: 0.9mm; border-top: 0.3mm solid ${TEXT_GRAY}; padding-top: 0.8mm; }
    .signature-line { width: 50%; border-bottom: 0.3mm solid ${TEXT_GRAY}; height: 2.8mm; margin-bottom: 0.4mm; }
    @media print {
      body { background: #fff; }
      .sheet { padding: 0; }
      .card-pair { justify-content: flex-start; }
      .id-card, .id-card * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
    }
  `;
}

/**
 * @param {{ member: object, photoDataUrl?: string|null, qrDataUrl?: string|null }} card
 * @param {{ inlinePublicAssets?: { ndcDataUrl?: string, teinDataUrl?: string } }} [options]
 */
export function buildSingleIdCardHtml(card, options = {}) {
  const inlinePublicAssets = options.inlinePublicAssets || {};
  const body = `
    <section class="sheet">
      <div class="card-pair">
        ${renderFront(card.member, card.photoDataUrl, card.qrDataUrl, inlinePublicAssets)}
        ${renderBack()}
      </div>
    </section>
  `;
  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>ATUTEIN ID Card</title>
  <style>${baseStyles()}</style>
</head>
<body>${body}</body>
</html>`;
}
